import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { Pill } from "@/components/common";
import { AlertTriangle, RotateCw, CalendarClock, X, FileSpreadsheet } from "lucide-react";

// Login prompt for department heads: shows submittals due today / overdue / stuck.
// Reappears each day (dismissal is keyed to the current date in sessionStorage).
export default function TrackerPrompts() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/tracker/prompts/me");
        if (!alive) return;
        setData(data);
        const key = `tracker-prompt-dismissed-${new Date().toISOString().slice(0, 10)}`;
        if (data.total > 0 && sessionStorage.getItem(key) !== "1") setOpen(true);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, []);

  const dismiss = () => {
    const key = `tracker-prompt-dismissed-${new Date().toISOString().slice(0, 10)}`;
    sessionStorage.setItem(key, "1");
    setOpen(false);
  };

  if (!data || data.total === 0) return null;

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" data-testid="tracker-prompt-modal">
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12 }}
            className="w-full max-w-lg card-surface p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileSpreadsheet size={20} className="text-gold" />
                <div>
                  <h3 className="font-heading text-xl">Submittals need your attention</h3>
                  <div className="text-sm text-muted-foreground">{data.total} item(s) due today, overdue, or in resubmission.</div>
                </div>
              </div>
              <button onClick={dismiss} data-testid="tracker-prompt-dismiss" className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"><X size={18} /></button>
            </div>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {data.prompts.map((p) => (
                <div key={`${p.project_id}-${p.dept}`} data-testid={`tracker-prompt-row-${p.job_code}`}
                  className="rounded-md border border-border bg-background/50 p-3 flex items-center gap-2 flex-wrap">
                  <div className="flex-1 min-w-[150px]">
                    <div className="text-sm font-medium">{p.project_name}</div>
                    <div className="text-[11px] text-muted-foreground">{p.stage}</div>
                  </div>
                  {p.overdue > 0 && <Pill tone="critical"><AlertTriangle size={11} /> {p.overdue} overdue</Pill>}
                  {p.stuck > 0 && <Pill tone="gold"><RotateCw size={11} /> {p.stuck} resubmission</Pill>}
                  {p.due_today > 0 && <Pill tone="info"><CalendarClock size={11} /> {p.due_today} due today</Pill>}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={dismiss} className="px-4 py-2 rounded-md text-sm border border-border hover:bg-muted">Later</button>
              <button onClick={() => { dismiss(); navigate("/projects"); }} data-testid="tracker-prompt-goto"
                className="px-4 py-2 rounded-md text-sm bg-gold text-primary-foreground font-semibold hover:opacity-90">
                Update submittals
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
